import { Component, OnInit, Input, EventEmitter, Output, OnChanges, SimpleChanges } from '@angular/core';
import { Observable, of, timer } from 'rxjs';
import { map, tap, filter } from 'rxjs/operators';
import moment from 'moment/moment';

@Component({
  selector: 'fgbcl-countdown',
  templateUrl: './countdown.component.html',
  styleUrls: ['./countdown.component.scss'],
})
export class CountdownComponent implements OnInit, OnChanges {
  /** The end date time for the countdown in UTC format. */
  @Input() endDateTime: string;

  /** The time that the end date was requested - used to return time remaining more accurately than using moment.utc */
  @Input() requestDateTime: string = '';

  /** The text to display in the countdown upon expiry. */
  @Input() completeText: string = 'Complete';

  /** determines whether component will show the timer or not.
   * (Will be useful for navigate back as soon as item's end time reached */
  @Input() showTimer: boolean = true;

  /** determines whether component will receive local or utc time. default is utc. */
  @Input() isUTC: boolean = true;

  /** Emitted upon countdown expiry. */
  @Output() complete = new EventEmitter<any>();

  //display the minutes
  @Input() displayMinutes: boolean = false;

  formattedTime$: Observable<string>;
  daysRemaining: number;
  completed = false;
  private endDate: moment.Moment;
  private requestDate: moment.Moment;

  constructor() {}

  ngOnInit(): void {
    this.setTimer(this.endDateTime);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.endDateTime) {
      this.setTimer(changes.endDateTime.currentValue);
    }
  }

  /**
   * Sets the countdown timer relative to the supplied endDateTime.
   * @param endDateTime The time that the countdown should end as a UTC date.
   */
  setTimer(endDateTime: string): void {
    this.endDate = this.isUTC ? moment.utc(endDateTime) : moment.utc(new Date(endDateTime).toUTCString());
    this.setRequestDate();

    if (this.endDate < this.requestDate) {
      this.completed = true;
      this.complete.emit(this.completed);
      this.formattedTime$ = of(this.completeText);
    } else {
      this.completed = false;
      this.formattedTime$ = timer(0, 1000).pipe(
        // convert interval into remaining time in seconds
        map(() => {
          this.setRequestDate();
          let utcNow = this.requestDate;
          return Math.floor(this.endDate.diff(utcNow, 'seconds'));
        }),
        // stop emission after complete
        filter(() => !this.completed),
        // emit on complete
        tap((t) => {
          if (t <= 0) {
            this.completed = true;
            this.complete.emit(this.completed);
          }
        }),
        map((seconds) => this.formatRemainingTime(seconds))
      );
    }
  }

  private setRequestDate(): void {
    this.requestDate = this.requestDateTime.length > 0 ? moment(this.requestDateTime) : moment.utc();
    this.requestDate = this.isUTC ? moment.utc(this.requestDate) : moment.utc();
  }

  formatRemainingTime(seconds: number): string {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor(seconds / 60);
    if (days) {
      const remainderHours = Math.floor((seconds % 86400) / 3600);
      const remainderMinutes = Math.floor((seconds % 3600) / 60);
      const dFormat: string = getDayFormatByLcid();

      return this.displayMinutes
        ? `${days}${dFormat} ${remainderHours}H ${remainderMinutes}M`
        : `${days}${dFormat} ${remainderHours}H`;
    } else if (hours) {
      const remainderMinutes = Math.floor((seconds % 3600) / 60);
      return `${hours}H ${remainderMinutes}M`;
    } else if (minutes) {
      const remainderSeconds = seconds % 60;
      return `${minutes}M ${remainderSeconds}S`;
    } else {
      return `${seconds}S`;
    }
  }
}

// function to change days letter to correct language according to current lcid set
export function getDayFormatByLcid(): string {
  let lcid = localStorage.getItem('lcid');
  switch (lcid) {
    case '3084':
    case '1036':
      return 'J';
    default:
      return 'D';
  }
}
